import * as m from '@staiter/i18n';
import { Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { ChatDrawer } from '~/components/chat/ChatDrawer';
import { RouteLoadingSkeleton } from '~/components/data';
import { IntegrationStatusBanner } from '~/components/domain/IntegrationStatusBanner';
import { AggregateModeBanner } from '~/components/layout/AggregateModeBanner';
import { AsyncExportsTray } from '~/components/layout/AsyncExportsTray';
import { CommandPalette } from '~/components/layout/CommandPalette';
import { EntityPickerEmptyState as EntityPickerEmptyStateBanner } from '~/components/layout/EntityPickerEmptyState';
import { HelpOverlay } from '~/components/layout/HelpOverlay';
import { ImpersonationBanner } from '~/components/layout/ImpersonationBanner';
import { MobileBottomNav } from '~/components/layout/MobileBottomNav';
import { ModuleGuard } from '~/components/layout/ModuleGuard';
import { Sidebar } from '~/components/layout/Sidebar';
import { TopBar } from '~/components/layout/TopBar';
import { NotificationCard } from '~/components/tasks/notifications/NotificationCard';
import { fetchMe } from '~/lib/api/auth';
import { tokenStorage } from '~/lib/api/client';
import { useMainSurfaceAvail } from '~/lib/chat/useMainSurfaceAvail';
import { useShortcut } from '~/lib/shortcuts/useShortcuts';
import { useBusinessMidnightInvalidation } from '~/lib/tasks/business-day';
import { useIsMobile } from '~/lib/useMediaQuery';
import { useSidebarServerSync } from '~/lib/useSidebarServerSync';
import { useAuthStore } from '~/store/auth';
import { useZenModeStore } from '~/store/zenMode';
import styles from './_auth.module.css';

export const Route = createFileRoute('/_auth')({
  beforeLoad: () => {
    const access = tokenStorage.getAccess();
    if (!access) {
      throw redirect({ to: '/login' });
    }
    if (useAuthStore.getState().mustReset) {
      throw redirect({ to: '/force-password-change' });
    }
  },
  component: AuthLayout,
  // FE-41: показывать skeleton при route-transition после 150ms,
  // не позволяя пользователю видеть stale content предыдущего route.
  pendingComponent: () => (
    <div className={styles.shell}>
      <main className={styles.main}>
        <RouteLoadingSkeleton />
      </main>
    </div>
  ),
  pendingMs: 150,
  pendingMinMs: 300,
});

function AuthLayout(): JSX.Element {
  const [helpOpen, setHelpOpen] = useState(false);
  /**
   * chat-panel-geometry F-4 — `.main` замеряется, а не вычисляется: место под
   * полотно и панель вместе знает только он сам (SOLUTION §2.1).
   */
  const mainRef = useRef<HTMLElement>(null);
  useMainSurfaceAvail(mainRef);
  const [cmdOpen, setCmdOpen] = useState(false);
  const isMobile = useIsMobile();
  const user = useAuthStore((s) => s.user);
  const setMe = useAuthStore((s) => s.setMe);
  // Full-bleed routes own their own padding/sidebar (e.g. DocsLayout).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isFullBleed = pathname.startsWith('/docs');
  // S17.1 F-A1 — workspace surface для task-module видов.
  // projects-table-consistency F1 — `/projects` (список) тоже full-bleed workspace-шелл
  // (паритет с /work-tasks). Детальные `/projects/:id`, `/projects/new` — обычный контент.
  const isProjectsList = pathname === '/projects';
  const isWorkspace = pathname.startsWith('/work-tasks') || isProjectsList;
  // Группа П (SOLUTION §6) — zen-режим ганта: скрываем весь хром приложения
  // (Sidebar/TopBar/баннеры). conditional render, НЕ display:none — чистый фокус-порядок.
  const zen = useZenModeStore((s) => s.zen);
  // F94 (§9.5): в деловую полночь списки и счётчики задач устаревают — «просрочено» и
  // «сегодня» переключаются без перезагрузки вкладки.
  useBusinessMidnightInvalidation();

  // 2026-06-08 — per-user sidebar layout (collapse / reorder / rename / custom sections).
  useSidebarServerSync();

  // Hydrate /me после логина: setFromLogin сохраняет tokens, но user=null.
  // F9 — убран silent catch: 401/403 → redirect на login; прочее → toast через QueryClient onError.
  useEffect(() => {
    if (!user) {
      void fetchMe()
        .then(async (me) => {
          setMe(me);
          // FM-S-multi-4-6 — sync multi-tenancy state from /auth/me.
          const { useActiveEntityStore } = await import('~/store/active-entity');
          useActiveEntityStore.getState().hydrateFromMe(me as never);
          // ACC-B1 (С-I, F108): сервер ответил по компании «по умолчанию», а стор выбрал другую
          // (сохранённую в браузере) — профиль дочитывается по выбранной, иначе меню и экран
          // отказа судят модулями чужой компании.
          // С-I2 Р.3: в «Все компании» и в холдинге сервер считает роль по компании по умолчанию —
          // дочитывать по выбранной нечего.
          const st = useActiveEntityStore.getState();
          if (
            st.activeScope === 'entity' &&
            st.activeEntityId &&
            st.activeEntityId !== me.active_entity_id
          ) {
            setMe(await fetchMe());
          }
        })
        .catch((err: unknown) => {
          if (err && typeof err === 'object' && 'status' in err) {
            const status = (err as { status: number }).status;
            if (status === 401 || status === 403) {
              // middleware _auth.tsx уже делает redirect; здесь просто подавляем spam toast.
              return;
            }
          }
          // eslint-disable-next-line no-console
          console.warn('fetchMe failed', err);
        });
    }
  }, [user, setMe]);

  useShortcut(
    'Escape',
    () => {
      setHelpOpen(false);
      setCmdOpen(false);
    },
    'Закрыть оверлей',
  );

  function focusSearch(): void {
    // stub: open command palette как глобальный search в S5.
    setCmdOpen(true);
  }

  return (
    <div className={`${styles.shell} ${isWorkspace ? styles.workspaceShell : ''}`}>
      {/* Группа П (§6.1) — zen прячет ВЕСЬ верхний/боковой хром приложения; остаются
          только порталы (карточка задачи, поповеры, тосты) и сам гант-блок. */}
      {!zen && <ImpersonationBanner />}
      {!zen && (
        <TopBar
          onOpenSearch={focusSearch}
          onOpenHelp={() => setHelpOpen(true)}
          onOpenCommandPalette={() => setCmdOpen(true)}
        />
      )}
      {!zen && <IntegrationStatusBanner />}
      {/* FM-S-multi-4-20 / FM-S-multi-4-21 — multi-tenancy banners. */}
      {!zen && <AggregateModeBanner />}
      {!zen && <EntityPickerEmptyStateBanner />}
      <div className={styles.body}>
        {!zen && <Sidebar />}
        <main
          ref={mainRef}
          className={`${styles.main} ${isFullBleed ? styles.fullBleed : ''} ${isWorkspace ? styles.workspace : ''}`}
        >
          <ModuleGuard>
            <Outlet />
          </ModuleGuard>
          {/*
            chat-panel-geometry F-11, круг 2 кадра К1 — направляющая границы
            уступки живёт ВНУТРИ рабочей области, а не поверх всего экрана.

            Круг 1 (UX-2, дословно): «Направляющая нема и не отделена по смыслу —
            сплошная тёплая линия во всю высоту, без подписи, идёт и через
            верхнюю панель, которая полотном не является». Внутри `.main` линия
            ограничена именно полотном по построению, без арифметики от высоты
            шапки, которая врёт в zen-режиме и под баннером.
          */}
          <div className={styles.boundaryGuide} aria-hidden="true">
            <span className={styles.boundaryGuideLabel}>{m.chat_boundary_guide_label()}</span>
          </div>
        </main>
      </div>
      {isMobile && !zen && <MobileBottomNav />}
      {/* notification-center F11 (раунд 2) — карточка уведомления, единый хост для
          дропдауна и табличного центра (открытие через notificationCardStore). */}
      <NotificationCard />
      <AsyncExportsTray />
      <ChatDrawer />
      <HelpOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </div>
  );
}
